import { useMemo } from "react";
import useSWR from "swr";

import graphQLFetcher, {
  UpcomingBoardMeetingsDocument,
} from "@liberty-fund/graphql-client";
import { formatDate } from "@liberty-fund/utils/formatting";

interface NextBoardMeetingData {
  title: string;
  eyebrow: string;
}

/**
 * Next board meeting for the Dashboard card.
 *
 * `upcomingBoardMeetings` was already in the schema; the `/api/dashboard/
 * next-board-meeting` route existed only to attach the ALB's bearer token and
 * reshape the result. Both now happen here.
 */
export function useGetNextBoardMeeting() {
  const { data, isLoading, error, isValidating } = useSWR(
    "upcomingBoardMeetings",
    // The Dashboard only ever renders one.
    () => graphQLFetcher(UpcomingBoardMeetingsDocument, { limit: 1 }),
    {
      // The card is non-critical; don't retry-storm on auth or server errors.
      shouldRetryOnError: false,
    },
  );

  const next = data?.upcomingBoardMeetings?.[0];

  const memoizedValue = useMemo(() => {
    const nextBoardMeeting: NextBoardMeetingData | Record<string, never> = next
      ? {
          eyebrow: "Next Board Meeting",
          title: formatDate(next.startDate) ?? "Date Pending",
        }
      : {};

    return {
      nextBoardMeeting: nextBoardMeeting as NextBoardMeetingData,
      nextBoardMeetingLoading: isLoading,
      nextBoardMeetingError: error,
      nextBoardMeetingValidating: isValidating,
      // Only treat "no data" as empty when there was no error — otherwise an
      // auth failure silently presents as "No upcoming Board Meeting".
      nextBoardMeetingEmpty: !isLoading && !next && !error,
    };
  }, [next, error, isLoading, isValidating]);

  return memoizedValue;
}
